import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import {
  FALLBACK_LANGUAGE,
  Language,
  LANGUAGE_LOCALE_MAP,
  SUPPORTED_LANGUAGES,
  TRANSLATION_KEY_INDEX,
  translations,
} from '../i18n/translations';

type SelectOptions = Partial<Record<Language, string>> & {
  fallback?: string;
  default?: string;
  translationKey?: string;
};

interface LanguageContextValue {
  language: Language;
  setLanguage: (language: Language) => void;
  t: (key: string, fallback?: string) => string;
  availableLanguages: Language[];
  select: (options: SelectOptions) => string;
  locale: string;
}

const LanguageContext = createContext<LanguageContextValue | undefined>(undefined);

const STORAGE_KEY = 'soartistic_language';

const normalize = (value?: string): string => (value ?? '').trim().toLowerCase();

const buildOptionsSignature = (options: SelectOptions): string => {
  const parts: string[] = [];

  SUPPORTED_LANGUAGES.forEach((lang) => {
    const optionValue = options[lang];
    if (typeof optionValue === 'string') {
      parts.push(`${lang}:${optionValue}`);
    }
  });

  if (options.default) {
    parts.push(`default:${options.default}`);
  }

  if (options.fallback) {
    parts.push(`fallback:${options.fallback}`);
  }

  if (options.translationKey) {
    parts.push(`key:${options.translationKey}`);
  }

  return parts.join('|');
};

const translationKeyCache = new Map<string, string | null>();
const missingKeyWarnings = new Set<string>();

const isDevEnvironment = (() => {
  if (typeof import.meta !== 'undefined' && import.meta.env) {
    return Boolean(import.meta.env.DEV);
  }

  if (typeof process !== 'undefined' && process.env && process.env.NODE_ENV) {
    return process.env.NODE_ENV === 'development';
  }

  return false;
})();

const findTranslationKey = (options: SelectOptions): string | undefined => {
  if (options.translationKey) {
    return options.translationKey;
  }

  const signature = buildOptionsSignature(options);
  if (translationKeyCache.has(signature)) {
    const cached = translationKeyCache.get(signature);
    return cached ?? undefined;
  }

  const englishValue = options.en ?? options.default ?? options.fallback;
  if (!englishValue) {
    translationKeyCache.set(signature, null);
    if (isDevEnvironment && !missingKeyWarnings.has(signature)) {
      missingKeyWarnings.add(signature);
      console.warn('[i18n] Unable to derive translation key for select()', options);
    }
    return undefined;
  }

  const normalizedEn = normalize(englishValue);
  if (!normalizedEn) {
    translationKeyCache.set(signature, null);
    if (isDevEnvironment && !missingKeyWarnings.has(signature)) {
      missingKeyWarnings.add(signature);
      console.warn('[i18n] Unable to normalize English value for select()', options);
    }
    return undefined;
  }

  const languagesWithValues = SUPPORTED_LANGUAGES.filter((lang) => lang !== 'en' && typeof options[lang] === 'string');

  let candidateKeys: string[] = [];

  for (const lang of languagesWithValues) {
    const optionValue = options[lang];
    const compositeKey = `${normalizedEn}||${lang}:${normalize(optionValue)}`;
    const compositeMatches = TRANSLATION_KEY_INDEX[compositeKey];
    if (compositeMatches && compositeMatches.length > 0) {
      candidateKeys = compositeMatches;
      break;
    }
  }

  if (candidateKeys.length === 0) {
    candidateKeys = TRANSLATION_KEY_INDEX[normalizedEn] ?? [];
  }

  const resolvedKey = candidateKeys.find((key) =>
    SUPPORTED_LANGUAGES.every((lang) => {
      const optionValue = options[lang];
      if (typeof optionValue !== 'string') {
        return true;
      }

      const translationValue = translations[lang]?.[key];
      return normalize(translationValue) === normalize(optionValue);
    })
  ) ?? candidateKeys[0] ?? null;

  translationKeyCache.set(signature, resolvedKey);

  if (!resolvedKey && isDevEnvironment && !missingKeyWarnings.has(signature)) {
    missingKeyWarnings.add(signature);
    console.warn('[i18n] No matching translation key found for select()', options);
  }

  return resolvedKey ?? undefined;
};

const getInitialLanguage = (): Language => {
  if (typeof window === 'undefined') {
    return FALLBACK_LANGUAGE;
  }

  const stored = localStorage.getItem(STORAGE_KEY) as Language | null;
  if (stored && SUPPORTED_LANGUAGES.includes(stored)) {
    return stored;
  }

  const browserLanguage = navigator.language?.split('-')[0] as Language | undefined;
  if (browserLanguage && SUPPORTED_LANGUAGES.includes(browserLanguage)) {
    return browserLanguage;
  }

  return FALLBACK_LANGUAGE;
};

export const LanguageProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [language, setLanguageState] = useState<Language>(getInitialLanguage);

  useEffect(() => {
    if (typeof window !== 'undefined') {
      localStorage.setItem(STORAGE_KEY, language);
    }
  }, [language]);

  const setLanguage = useCallback((lang: Language) => {
    if (SUPPORTED_LANGUAGES.includes(lang)) {
      setLanguageState(lang);
    }
  }, []);

  const translate = useCallback(
    (key: string, fallback?: string) => {
      const order: Language[] = [language];

      if (language !== 'en') {
        order.push('en');
      }

      if (!order.includes(FALLBACK_LANGUAGE)) {
        order.push(FALLBACK_LANGUAGE);
      }

      SUPPORTED_LANGUAGES.forEach((lang) => {
        if (!order.includes(lang)) {
          order.push(lang);
        }
      });

      for (const lang of order) {
        const value = translations[lang]?.[key];
        if (value) {
          return value;
        }
      }

      return fallback ?? key;
    },
    [language]
  );

  const select = useCallback(
    (options: SelectOptions) => {
      const translationKey = findTranslationKey(options);

      if (translationKey) {
        const fallbackValue = [
          typeof options[language] === 'string' ? options[language] : undefined,
          options.en,
          options.default,
          options.fallback,
        ].find((value): value is string => typeof value === 'string' && value.length > 0);

        const translated = translate(
          translationKey,
          fallbackValue ?? options.en ?? options.default ?? options.fallback ?? translationKey
        );
        if (translated) {
          return translated;
        }
      }

      const preferredOrder: Language[] = [];

      if (!preferredOrder.includes(language)) {
        preferredOrder.push(language);
      }

      if (language !== 'en' && !preferredOrder.includes('en')) {
        preferredOrder.push('en');
      }

      if (!preferredOrder.includes(FALLBACK_LANGUAGE)) {
        preferredOrder.push(FALLBACK_LANGUAGE);
      }

      SUPPORTED_LANGUAGES.forEach((lang) => {
        if (!preferredOrder.includes(lang)) {
          preferredOrder.push(lang);
        }
      });

      for (const lang of preferredOrder) {
        const value = options[lang];
        if (typeof value === 'string' && value.length > 0) {
          return value;
        }
      }

      if (options.default) {
        return options.default;
      }

      if (options.fallback) {
        return options.fallback;
      }

      const firstExplicit = Object.entries(options)
        .filter(([key]) => key !== 'fallback' && key !== 'default' && key !== 'translationKey')
        .map(([, value]) => value)
        .find((value): value is string => typeof value === 'string');

      return firstExplicit ?? '';
    },
    [language, translate]
  );

  const value = useMemo(
    () => ({
      language,
      setLanguage,
      t: translate,
      availableLanguages: SUPPORTED_LANGUAGES,
      select,
      locale: LANGUAGE_LOCALE_MAP[language] ?? LANGUAGE_LOCALE_MAP[FALLBACK_LANGUAGE],
    }),
    [language, setLanguage, select, translate]
  );

  return <LanguageContext.Provider value={value}>{children}</LanguageContext.Provider>;
};

export const useLanguage = (): LanguageContextValue => {
  const context = useContext(LanguageContext);
  if (!context) {
    throw new Error('useLanguage must be used within a LanguageProvider');
  }
  return context;
};
